import { useQueryClient } from '@tanstack/react-query';
import maxBy from 'lodash/maxBy';
import React, { useCallback } from 'react';
import { LuCircleCheckBig } from 'react-icons/lu';
import { toast } from 'sonner';

import { CashFlowType, DataType, FrequencyType } from '@ndvr/portfolio-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, nextWorkingDay } from 'utils';
import { formatCurrency } from 'utils/number-formatter';
import { useAccountGroup } from 'views/portfolio/account-group-queries';
import { useCreateCashFlowConfig } from 'views/portfolio/cash-flow-queries';
import { Toast } from 'widgets';

import { RmdOwnerGroup } from './rmd-utils';

export interface FulfillRmdModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    owner: RmdOwnerGroup;
}

export const FulfillRmdModal: React.FC<FulfillRmdModalProps> = ({ open, onOpenChange, owner }) => {
    const { totalRmdObligation, totalYtdSatisfied, totalPlannedWithdrawals } = owner;

    const ytd = totalYtdSatisfied ?? 0;
    const remainingWithPlanned = Math.max(0, totalRmdObligation - ytd - totalPlannedWithdrawals);
    const alreadySatisfied = remainingWithPlanned === 0;

    // Use the account with the largest current NAV as the suggested source account
    const sourceAccount = maxBy(owner.accounts, (acc) => parseFloat(acc.currentNav ?? '0'));

    const { data: accountGroup, isLoading: isAccountGroupLoading } = useAccountGroup(
        open ? sourceAccount?.accountGroupGUID : undefined,
    );

    const { mutate: createCashFlowConfig, isPending } = useCreateCashFlowConfig();
    const queryClient = useQueryClient();

    const ownerName = `${owner.firstName} ${owner.lastName}`.trim();

    const handleCreateWithdrawal = useCallback(() => {
        if (!sourceAccount || !accountGroup) return;
        createCashFlowConfig(
            {
                accountGroupGUID: sourceAccount.accountGroupGUID,
                cashFlowConfig: {
                    accountGUID: sourceAccount.accountGUID,
                    amount: String(remainingWithPlanned),
                    cashFlowType: CashFlowType.WITHDRAWAL,
                    inflationConfig: { inflationAdjusted: false },
                    factorDateFrame: {
                        startDate: formatDate(nextWorkingDay(), 'SERVER'),
                        frequencyType: FrequencyType.ONE_TIME,
                    },
                    title: {
                        encryptionContextGUID: accountGroup.householdGUID,
                        plainText: 'RMD Withdrawal',
                        dataType: DataType.SI_FACTOR_TITLE,
                    },
                },
            },
            {
                onSuccess: () => {
                    toast.success('Withdrawal created successfully');
                    queryClient.invalidateQueries({ queryKey: ['rmd'] });
                    onOpenChange(false);
                },
                onError: (error) => Toast.forError(error, 'Failed to create withdrawal'),
            },
        );
    }, [sourceAccount, accountGroup, remainingWithPlanned, createCashFlowConfig, queryClient, onOpenChange]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuCircleCheckBig className="h-4 w-4" />
                        Fulfill Remaining RMD
                    </DialogTitle>
                    <DialogDescription>
                        Create a one-time withdrawal to satisfy {ownerName}&apos;s remaining RMD obligation.
                    </DialogDescription>
                </DialogHeader>

                {alreadySatisfied ? (
                    <div className="py-4 text-center">
                        <Badge className="border-green-300 bg-green-100 px-3 py-1 text-sm text-green-800">
                            RMD Already Satisfied
                        </Badge>
                        <p className="mt-3 text-sm text-gray-500">
                            No additional withdrawal is needed. The RMD obligation is fully covered by actual and
                            planned distributions.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        {/* Summary breakdown */}
                        <div className="space-y-1.5 rounded-md border bg-gray-50 p-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">RMD Obligation</span>
                                <span className="font-medium">{formatCurrency(totalRmdObligation)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">YTD Satisfied</span>
                                <span className="font-medium">
                                    {totalYtdSatisfied !== undefined ? formatCurrency(ytd) : '—'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Planned Withdrawals</span>
                                <span className="font-medium">
                                    {totalPlannedWithdrawals > 0 ? formatCurrency(totalPlannedWithdrawals) : '—'}
                                </span>
                            </div>
                            <div className="flex justify-between border-t pt-1.5 font-semibold">
                                <span>Remaining to Fulfill</span>
                                <span>{formatCurrency(remainingWithPlanned)}</span>
                            </div>
                        </div>

                        {/* Withdrawal amount — read-only, pre-filled */}
                        <div className="space-y-2">
                            <Label htmlFor="fulfill-withdrawal-amount">Withdrawal Amount</Label>
                            <Input
                                id="fulfill-withdrawal-amount"
                                type="text"
                                value={formatCurrency(remainingWithPlanned)}
                                readOnly
                                className="bg-gray-50"
                            />
                        </div>

                        {/* Source account — largest by current NAV */}
                        {sourceAccount && (
                            <div className="space-y-2">
                                <Label>Source Account</Label>
                                <div className="rounded-md border bg-gray-50 p-3">
                                    <div className="text-sm font-medium">
                                        {sourceAccount.accountName ?? sourceAccount.pId}
                                    </div>
                                    {(sourceAccount.custodyType || sourceAccount.custodyAccountId) && (
                                        <div className="mt-0.5 text-xs text-gray-500">
                                            {[
                                                sourceAccount.custodyType,
                                                sourceAccount.custodyAccountId
                                                    ? `****${sourceAccount.custodyAccountId.slice(-4)}`
                                                    : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </div>
                                    )}
                                    {sourceAccount.currentNav && (
                                        <div className="mt-0.5 text-xs text-gray-500">
                                            Current balance: {formatCurrency(sourceAccount.currentNav)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Frequency — always one-time */}
                        <div className="space-y-2">
                            <Label>Frequency</Label>
                            <Input type="text" value="One-time" readOnly className="bg-gray-50" />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    {!alreadySatisfied && (
                        <Button
                            onClick={handleCreateWithdrawal}
                            disabled={isPending || isAccountGroupLoading || !accountGroup}
                        >
                            {isPending ? 'Creating…' : 'Create Withdrawal'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
